import { type ComponentProps, forwardRef, type PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Slot } from '@radix-ui/react-slot'
import { type RecipeVariants } from '@vanilla-extract/recipes'
import { Divider } from '../Divider/Divider'
import { Heading } from '../Heading/Heading'
import { Text } from '../Text/Text'
import { cardAside, cardDivider, cardHeader, cardRibbon, cardRoot } from './Card.css'

type RootStyleProps = RecipeVariants<typeof cardRoot>
type RootProps = ComponentProps<'article'> & RootStyleProps & { asChild?: boolean }
const CardRoot = forwardRef(function CardRoot(
  { variant, size, className, children, asChild, ...props }: RootProps,
  ref: RootProps['ref'],
) {
  const Component = asChild ? Slot : 'article'
  return (
    <Component className={clsx(cardRoot({ variant, size }), className)} {...props} ref={ref}>
      {children}
    </Component>
  )
})

type HeaderProps = ComponentProps<'header'>
function CardHeader({ className, children, ...props }: HeaderProps) {
  return (
    <header className={clsx(cardHeader, className)} {...props}>
      {children}
    </header>
  )
}

type RibbonProps = ComponentProps<'div'>
function CardRibbon({ className, children, ...props }: RibbonProps) {
  return (
    <div className={clsx(cardRibbon, className)} {...props}>
      {children}
    </div>
  )
}
function CardMedia({ children }: PropsWithChildren) {
  // Slot ensures only one element is passed as children
  return <Slot>{children}</Slot>
}

type HeadingProps = ComponentProps<'div'>
function CardHeading({ className, children, ...props }: HeadingProps) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

type TitleProps = ComponentProps<typeof Heading>
function CardTitle({ className, children, ...props }: TitleProps) {
  return (
    <Heading variant="standard.18" className={className} {...props}>
      {children}
    </Heading>
  )
}

type SubtitleProps = ComponentProps<typeof Text>
function CardSubtitle({ className, children, ...props }: SubtitleProps) {
  return (
    <Text color="textSecondary" className={className} {...props}>
      {children}
    </Text>
  )
}

type AsideProps = ComponentProps<typeof Slot> & { floating?: boolean }
function CardAside({ className, children, floating, ...props }: PropsWithChildren<AsideProps>) {
  return (
    <Slot className={clsx(cardAside({ floating }), className)} {...props}>
      {children}
    </Slot>
  )
}

type ContentProps = ComponentProps<'div'> & { asChild?: boolean }
function CardContent({ className, children, asChild, ...props }: PropsWithChildren<ContentProps>) {
  const Component = asChild ? Slot : 'div'

  return (
    <Component className={className} {...props}>
      {children}
    </Component>
  )
}

type DividerProps = ComponentProps<typeof Divider>
function CardDivider({ className, ...props }: DividerProps) {
  return <Divider className={clsx(cardDivider, className)} {...props} />
}

export const Card = {
  Root: CardRoot,
  Ribbon: CardRibbon,
  Header: CardHeader,
  Media: CardMedia,
  Heading: CardHeading,
  Title: CardTitle,
  Subtitle: CardSubtitle,
  Aside: CardAside,
  Content: CardContent,
  Divider: CardDivider,
}
